import React, { useState, useEffect } from 'react';
import { FaPencilAlt, FaSave } from 'react-icons/fa';
import api from '../services/api';
import { getProfileImageUrl } from '../utils/getProfileImageUrl';
import '../styles/Settings.css';

const Settings = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    fullName: '',
    about: '',
    profileImage: '',
    password: ''
  });
  const [editableField, setEditableField] = useState(null);
  const [editPassword, setEditPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [initialProfileImage, setInitialProfileImage] = useState('');
  const [previewImage, setPreviewImage] = useState('');
  const [notification, setNotification] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [usernameError, setUsernameError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await api.get('/setting', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const profileImageUrl = getProfileImageUrl(response.data.profile_image_url);

        setFormData({
          username: response.data.username,
          email: response.data.email,
          fullName: response.data.full_name,
          about: response.data.bio,
          profileImage: profileImageUrl,
          password: ''
        });
        setInitialProfileImage(profileImageUrl);
        setPreviewImage(profileImageUrl);
      } catch (error) {
        console.error('Error fetching profile data:', error);
        setNotification('Profil bilgileri yüklenirken hata oluştu.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleFieldChange = (e) => {
    setFormData({
      ...formData,
      [editableField]: e.target.value,
    });
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imagePreviewUrl = URL.createObjectURL(file);
      setPreviewImage(imagePreviewUrl);
      
      setIsLoading(true);
      setNotification('Fotoğraf yükleniyor...');
      
      try {
        const updatedData = new FormData();
        updatedData.append('profile_image_url', file);
        const token = localStorage.getItem('token');
        const response = await api.put('/setting/image', updatedData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
        });
        const newImageUrl = getProfileImageUrl(response.data.profile_image_url);
        setInitialProfileImage(newImageUrl);
        setFormData({ ...formData, profileImage: newImageUrl });
        setNotification('Profil fotoğrafı başarıyla güncellendi!');
        
        setTimeout(() => {
          setNotification('');
        }, 3000);
      } catch (error) {
        console.error('Profil fotoğrafı güncellenirken hata:', error);
        setNotification('Profil fotoğrafı güncellenirken bir hata oluştu.');
        setPreviewImage(initialProfileImage);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleImageRemove = async () => {
    if (isLoading) return;
    
    setIsLoading(true);
    setNotification('Fotoğraf kaldırılıyor...');
    
    try {
      const updatedData = new FormData();
      updatedData.append('removeImage', 'true');
      const token = localStorage.getItem('token');
      const response = await api.put('/setting/image', updatedData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      });
      const defaultImageUrl = getProfileImageUrl('/uploads/profile/default_profile_photo.png');
      setInitialProfileImage(defaultImageUrl);
      setPreviewImage(defaultImageUrl);
      setFormData({ ...formData, profileImage: defaultImageUrl });
      setNotification('Profil fotoğrafı kaldırıldı!');
      
      setTimeout(() => {
        setNotification('');
      }, 3000);
    } catch (error) {
      console.error('Profil fotoğrafı kaldırılırken hata:', error);
      setNotification('Profil fotoğrafı kaldırılırken bir hata oluştu.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = () => {
    setShowPasswordModal(true);
  };

  const handlePasswordSubmit = async () => {
    if (isLoading) return;
    
    try {
      if (currentPassword.trim() === '') {
        setNotification('Lütfen mevcut şifrenizi girin.');
        return;
      }
      setIsLoading(true);
      
      const updatedData = {
        username: formData.username,
        email: formData.email,
        full_name: formData.fullName,
        bio: formData.about,
        current_password: currentPassword
      };
      
      if (editPassword && newPassword.trim() !== '') {
        updatedData.password_hash = newPassword;
      }
      
      const token = localStorage.getItem('token');
      await api.put('/setting', updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      setNotification('Profil başarıyla güncellendi!');
      setShowPasswordModal(false);
      setEditPassword(false);
      setEditableField(null);
      setNewPassword('');
      setCurrentPassword('');
      setUsernameError('');
      
      setTimeout(() => {
        setNotification('');
      }, 3000);
    } catch (error) {
      console.error('Profil güncellenirken hata:', error);
      
      if (error.response) {
        if (error.response.status === 401 || 
            (error.response.data && error.response.data.message === 'Girdiğiniz şifre hatalı') || 
            (error.response.data && error.response.data.message === 'InvalidPassword')) {
          setShowPasswordModal(true);
          setNotification('Girdiğiniz mevcut şifre hatalı! Lütfen tekrar deneyin.');
        } else if (error.response.data && error.response.data.message === 'Bu kullanıcı adı zaten mevcut') {
          setUsernameError('Bu kullanıcı adı zaten mevcut.');
          setShowPasswordModal(false);
        } else {
          setNotification('Profil güncellenirken bir hata oluştu: ' + (error.response.data.message || ''));
          setShowPasswordModal(false);
        }
      } else {
        setNotification('Profil güncellenirken bir hata oluştu.');
        setShowPasswordModal(false);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditableField(null);
    if (editableField === "username") {
      setUsernameError('');
    }
  };

  return (
    <div className="settings settings-container">
      <h1 className="settings-heading">Profil Bilgileriniz</h1>
      
      {notification && (
        <div className={`notification ${notification.includes('hata') || notification.includes('hatali') ? 'error' : 'success'}`}>
          {notification}
        </div>
      )}
      
      {isLoading && (
        <div className="loading-indicator">Yükleniyor...</div>
      )}
      
      <div className="profile-form">
        <div className="form-group">
          <label>Kullanıcı Adınız</label>
          {editableField === "username" ? (
            <>
              <input
                type="text"
                value={formData.username}
                onChange={handleFieldChange}
              />
              {usernameError && <p className="error">{usernameError}</p>}
              <button 
                className="cancel-button" 
                onClick={handleCancelEdit}
              >
                İptal
              </button>
            </>
          ) : (
            <div className="info-field">
              {formData.username}
              <FaPencilAlt onClick={() => {
                setEditableField("username");
                setUsernameError('');
              }} />
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label>E-Posta Adresiniz</label>
          {editableField === "email" ? (
            <>
              <input
                type="email"
                value={formData.email}
                onChange={handleFieldChange}
              />
              <button 
                className="cancel-button" 
                onClick={handleCancelEdit}
              >
                İptal
              </button>
            </>
          ) : (
            <div className="info-field">
              {formData.email}
              <FaPencilAlt onClick={() => setEditableField("email")} />
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label>Şifreniz</label>
          <div className="info-field">
            {editPassword ? (
              <>
                <input
                  type="password"
                  placeholder="Yeni Şifre"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
                <button 
                  className="cancel-button" 
                  onClick={() => {
                    setEditPassword(false);
                    setNewPassword('');
                  }}
                >
                  İptal
                </button>
              </>
            ) : (
              <>
                ********
                <FaPencilAlt onClick={() => setEditPassword(true)} />
              </>
            )}
          </div>
        </div>
        
        <div className="form-group">
          <label>Adınız Soyadınız</label>
          {editableField === "fullName" ? (
            <div className="editable-field">
              <input
                type="text"
                value={formData.fullName}
                onChange={handleFieldChange}
              />
              <button 
                className="cancel-button" 
                onClick={handleCancelEdit}
              >
                İptal
              </button>
            </div>
          ) : (
            <div className="info-field">
              {formData.fullName}
              <FaPencilAlt onClick={() => setEditableField("fullName")} />
            </div>
          )}
        </div>
        
        <div className="form-group">
          <label>Hakkında</label>
          {editableField === "about" ? (
            <div className="editable-field">
              <textarea
                value={formData.about}
                onChange={handleFieldChange}
              />
              <button 
                className="cancel-button" 
                onClick={handleCancelEdit}
              >
                İptal
              </button>
            </div>
          ) : (
            <div className="info-field">
              {formData.about || "Henüz bir hakkında bilgisi eklenmemiş."}
              <FaPencilAlt onClick={() => setEditableField("about")} />
            </div>
          )}
        </div>
        
        <div className="image-section">
          <div className="profile-picture-section">
            <h3>Profil Fotoğrafınız</h3>
            <div className="profile-image-container">
              <img src={previewImage} alt="Profile" className="profile-image" />
              {isLoading && <div className="image-loading-overlay">Yükleniyor...</div>}
            </div>
            <div className="image-buttons">
              <label className="image-button">
                Değiştir
                <input 
                  type="file" 
                  accept="image/*"
                  style={{ display: 'none' }} 
                  onChange={handleImageChange}
                  disabled={isLoading}
                />
              </label>
              <button 
                className="remove-button" 
                onClick={handleImageRemove}
                disabled={isLoading || previewImage.includes('default_profile_photo.png')}
              >
                Kaldır
              </button>
            </div>
          </div>
        </div>
        
        <button 
          className="save-button" 
          onClick={handleSave}
          disabled={isLoading || (!editableField && !editPassword)}
        >
          <FaSave /> Bilgilerimi Güncelle
        </button>
      </div>
      
      {showPasswordModal && (
        <div className="modal">
          <div className="modal-content">
            <h3>Mevcut Şifrenizi Girin</h3>
            <p>Değişiklikleri kaydetmek için mevcut şifrenizi girmeniz gerekmektedir.</p>
            
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              placeholder="Mevcut Şifre"
              disabled={isLoading}
              className={notification && notification.includes('şifre hatalı') ? 'input-error' : ''}
            />

            {notification && notification.includes('şifre hatalı') && (
              <div className="password-error">
                {notification}
              </div>
            )}

            <div className="modal-buttons">
              <button 
                onClick={handlePasswordSubmit}
                disabled={isLoading || !currentPassword.trim()}
              >
                Onayla
              </button>
              <button 
                onClick={() => {
                  setShowPasswordModal(false);
                  setCurrentPassword('');
                  setNotification('');
                }}
                disabled={isLoading}
              >
                İptal
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;